import React from 'react';
import { Menu as HLMenu } from '@headlessui/react';
import tw from 'twin.macro';
import Button, {
  Props as ButtonProps,
} from '@teetsh/app/src/components/Buttons/Button';
import ButtonLink from '@teetsh/app/src/components/Buttons/Link';
import useMenuContext from './useMenuContext';
import {
  BaseButtonProps,
  Intent,
} from '@teetsh/app/src/components/Buttons/BaseButtonProps';
import { Icon } from '@teetsh/app/src/components/Icons/icon';
import Popover from '@teetsh/app/src/components/Popover';
import { useTranslation } from 'react-i18next';
import { ConfirmationPopoverBody } from '@teetsh/app/src/components/ButtonWithConfirmation/ConfirmationPopoverBody';

interface WithConfirmationButtonProps {
  withConfirmation?: boolean;
  confirmationText?: string;
  confirmationDescription?: string;
  confirmationIcon?: Icon | 'none';
  popoverText?: string;
  confirmationButtonProps?: ButtonProps;
  portal?: boolean;
}

interface Props {
  text?: string | React.ReactNode;
  to?: string;
  children?: string | React.ReactNode;
  large?: boolean;
  disabled?: boolean;
  intent?: Intent;
  icon?: Icon;
  onClick?: (e: any) => any;
  className?: string;
  loading?: boolean;
}

export function MenuItemButton({
  icon,
  text,
  onClick,
  to,
  intent,
  large,
  className,
  buttonRef,
  withConfirmation,
  confirmationText,
  confirmationDescription,
  confirmationIcon,
  popoverText,
  portal = true,
  ...rest
}: {
  icon?: Icon;
  onClick?: (e: any) => any;
  text: string | React.ReactNode;
  large?: boolean;
  to?: string;
  intent?: Intent;
  className?: string;
  disabled?: boolean;
  buttonRef?: any;
} & BaseButtonProps &
  WithConfirmationButtonProps) {
  const { t } = useTranslation(['common']);
  if (to) {
    return (
      <ButtonLink
        to={to}
        fill
        minimal
        alignText="left"
        tw="rounded-none"
        intent={intent}
        large={large}
        className={className}
        innerRef={buttonRef}
        icon={icon}
        {...rest}
      >
        {text}
      </ButtonLink>
    );
  }

  if (withConfirmation) {
    return (
      <Popover fill portal={portal}>
        <Popover.Button
          large={large}
          minimal
          innerRef={buttonRef}
          alignText="left"
          intent={intent}
          tw="rounded-none"
          className={className}
          icon={icon}
          {...rest}
        >
          {text}
        </Popover.Button>
        <Popover.Panel size="2xl">
          <ConfirmationPopoverBody
            onConfirm={() => {
              if (onClick) {
                onClick(undefined);
              }
            }}
            bodyTitle={
              popoverText ||
              t('common:components.buttons.confirmDeleteQuestion')
            }
            bodyDescription={confirmationDescription}
            confirmButtonTitle={
              confirmationText || t('common:buttons.confirmDelete')
            }
            backButtonTitle={t('common:buttons.cancel')}
            intent={'danger'}
          />
        </Popover.Panel>
      </Popover>
    );
  }

  return (
    <Button
      fill
      large={large}
      minimal
      innerRef={buttonRef}
      alignText="left"
      onClick={onClick}
      intent={intent}
      tw="rounded-none"
      className={className}
      icon={icon}
      {...rest}
    >
      {text}
    </Button>
  );
}

export const menuItemStyle = (p: { active?: boolean; loading?: boolean }) => [
  p.active && !p.loading && tw`bg-neutral-supershy text-neutral-moderate`,
  !p.active && tw`text-neutral-moderate`,
  p.loading && tw`p-2`,
];

export function MenuItem(
  props: Props & BaseButtonProps & WithConfirmationButtonProps,
) {
  const {
    children,
    text,
    disabled,
    icon,
    onClick,
    to,
    intent,
    loading,
    className,
    iconSize,
    withConfirmation,
    ...rest
  } = props;
  const { large } = useMenuContext();
  return (
    <HLMenu.Item disabled={disabled}>
      {({ active }) => (
        <div css={menuItemStyle({ active, loading })}>
          {loading && <div className="tsh-skeleton" tw="h-6 w-56" />}
          {text && !loading ? (
            <MenuItemButton
              intent={intent}
              disabled={disabled}
              large={large}
              aria-label={props['aria-label']}
              text={text}
              to={to}
              onClick={onClick}
              className={className}
              icon={icon}
              iconSize={iconSize}
              css={!intent && tw`hover:bg-neutral-supershy`}
              withConfirmation={withConfirmation}
              {...rest}
            />
          ) : (
            children
          )}
        </div>
      )}
    </HLMenu.Item>
  );
}
